import type { CheckboxProps } from '@mui/material'
import { Checkbox, FormControl, FormControlLabel, FormHelperText } from '@mui/material'
import { forwardRef } from 'react'
import type { ControllerProps, FieldPath, FieldValues } from 'react-hook-form'
import { Controller, useFormContext } from 'react-hook-form'

export type CheckboxControllerProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = Omit<
  CheckboxProps,
  'name'
> & {
  name: TName
  label?: string
  // It enables showing field validation errors. Its default value is true
  showError?: boolean
  controllerProps?: Omit<ControllerProps<TFieldValues, TName>, 'render' | 'name'>
}

function CheckboxField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: Readonly<Omit<CheckboxControllerProps<TFieldValues, TName>, 'controllerProps'>>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  if (!props.label) {
    return <Checkbox checked={Boolean(props.value)} {...props} ref={ref} />
  }

  return (
    <FormControlLabel ref={ref} control={<Checkbox checked={Boolean(props.value)} {...props} />} label={props.label} />
  )
}

const CheckboxFieldWithRef = forwardRef(CheckboxField)

function CheckboxController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ label, name, showError = true, controllerProps, ...fieldProps }: CheckboxControllerProps<TFieldValues, TName>) {
  const { control } = useFormContext<TFieldValues>()

  return (
    <Controller
      control={control}
      name={name}
      {...controllerProps}
      render={({ field, fieldState: { error, invalid } }) =>
        showError ? (
          <FormControl error={invalid}>
            <CheckboxFieldWithRef {...field} checked={field.value ?? false} {...fieldProps} label={label} />

            {invalid && showError && <FormHelperText>{error?.message}</FormHelperText>}
          </FormControl>
        ) : (
          <FormControlLabel
            control={<Checkbox {...field} checked={field.value} name={name} {...fieldProps} />}
            label={label}
          />
        )
      }
    />
  )
}

export default CheckboxController
